import { useRef } from "react";
import { motion, useInView } from "framer-motion";
import { useCountUp } from "../hooks/useCountUp";
import "./About.css";

const skillCategories = [
  { label: "Java Stack",    color: "purple", skills: ["Java", "Spring Boot", "Spring JPA", "JUnit", "TestNG", "Mockito", "REST APIs", "SOAP"] },
  { label: "Frontend",      color: "cyan",   skills: ["ReactJS", "Single-Spa", "TypeScript", "JavaScript", "HTML5", "CSS3", "Bootstrap", "Sass"] },
  { label: "Cloud / AWS",   color: "teal",   skills: ["EC2", "Lambda", "ECS", "SNS", "S3", "CloudFront", "RDS", "DynamoDB", "Serverless"] },
  { label: "DevOps & CI/CD",color: "pink",   skills: ["Jenkins", "Bamboo", "TeamCity", "AWS DevOps", "Docker", "SonarQube", "Git", "BitBucket"] },
  { label: "GenAI & Python",color: "green",  skills: ["Copilot", "LangGraph", "CrewAI", "OpenAI API", "Python", "FastAPI", "Pandas", "AutoGen"] },
  { label: "Database",      color: "orange", skills: ["Oracle", "PostgreSQL", "MySQL", "MongoDB", "DynamoDB", "Redis"] },
];

const highlightData = [
  { value: "7+",   label: "Years Experience",   icon: "⚡" },
  { value: "1.5M+",label: "Transactions/Day",   icon: "🚀" },
  { value: "10k+", label: "Customers Impacted", icon: "📈" },
  { value: "8",    label: "Engineers Mentored", icon: "🎯" },
];

// Expo.out easing for premium feel
const EASE = [0.16, 1, 0.3, 1];

const tagVariants = {
  hidden: { opacity: 0, y: 8 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: EASE } },
};

export function About() {
  const highlightsRef = useRef(null);
  const highlightsInView = useInView(highlightsRef, { once: true });

  const count0 = useCountUp(highlightData[0].value, highlightsInView);
  const count1 = useCountUp(highlightData[1].value, highlightsInView);
  const count2 = useCountUp(highlightData[2].value, highlightsInView);
  const count3 = useCountUp(highlightData[3].value, highlightsInView);
  const counts = [count0, count1, count2, count3];

  return (
    <section className="about section" id="about">
      <div className="section-container">

        <motion.div
          className="about__header"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <span className="section-label">About Me</span>
          <h2 className="section-title">The person behind the code</h2>
          <p className="section-subtitle">
            Lead Engineer at Westpac Group with 7+ years of expertise spanning Java Spring Boot,
            ReactJS, AWS cloud-native architecture, and GenAI. Permanent Resident based in Sydney, Australia.
          </p>
        </motion.div>

        {/* Stat highlights with count-up */}
        <motion.div
          className="about__highlights"
          ref={highlightsRef}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-60px" }}
          variants={{ visible: { transition: { staggerChildren: 0.1 } } }}
        >
          {highlightData.map((h, i) => (
            <motion.div
              className="about__highlight"
              key={h.label}
              variants={{
                hidden: { opacity: 0, scale: 0.92 },
                visible: { opacity: 1, scale: 1, transition: { duration: 0.6, ease: EASE } },
              }}
            >
              <span className="about__highlight-icon">{h.icon}</span>
              <span className="about__highlight-value">{counts[i]}</span>
              <span className="about__highlight-label">{h.label}</span>
            </motion.div>
          ))}
        </motion.div>

        {/* Skills with staggered tag entrance + hover glow */}
        <motion.div
          className="about__skills"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <div className="about__skills-header">
            <h3 className="about__skills-title">Technical Skills</h3>
            <span className="about__skills-cert">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="9 11 12 14 22 4"/>
                <path d="M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"/>
              </svg>
              Oracle Cloud Infrastructure Certified
            </span>
          </div>

          {skillCategories.map((cat) => (
            <div className="about__skill-group" key={cat.label}>
              <span className={`about__skill-category about__skill-category--${cat.color}`}>
                {cat.label}
              </span>
              {/* Staggered tag entrance, 30ms between each per UI Pro Max stagger-sequence rule */}
              <motion.div
                className="about__skill-tags"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-40px" }}
                variants={{ visible: { transition: { staggerChildren: 0.03 } } }}
              >
                {cat.skills.map((skill) => (
                  <motion.span
                    className="about__tag"
                    key={skill}
                    variants={tagVariants}
                    whileHover={{
                      borderColor: "var(--primary)",
                      boxShadow: "0 0 12px rgba(163,166,255,0.35)",
                      y: -2,
                      transition: { duration: 0.15 },
                    }}
                  >
                    {skill}
                  </motion.span>
                ))}
              </motion.div>
            </div>
          ))}
        </motion.div>

      </div>
    </section>
  );
}
